<template>
    <div
        ref="card"
        :style="
            isDesktop && isMounted
                ? `transform-style: preserve-3d; transform: perspective(1000px) translateZ(0)`
                : ''
        "
        class="relative w-full overflow-hidden rounded-12 bg-black/5 bg-gradient-to-b from-white/10 to-accent/20 to-70% p-3 px-3 pt-3 transition-all duration-300 ease-out will-change-transform lg:shadow-card lg:hover:shadow-card-hover">
        <div
            ref="glow"
            style="
                background-image: radial-gradient(
                    circle at 50% -20%,
                    #ffffff22,
                    #0000000f
                );
            "
            class="pointer-events-none absolute inset-0 size-full opacity-0 transition-opacity duration-300" />

        <div class="z-20 flex h-full flex-col justify-between">
            <slot />
        </div>

        <div
            class="pointer-events-none absolute -bottom-24 left-1/2 aspect-square w-full -translate-x-1/2 rounded-full bg-[radial-gradient(circle,_var(--tw-gradient-stops))] from-accent/50 via-accent/10 to-transparent max-lg:hidden sm:w-[150%]" />
    </div>
</template>

<script setup lang="ts">
import { useWindowSize } from '@vueuse/core'

const { width } = useWindowSize()
const isMounted = ref(false)
const isDesktop = computed(() => {
    return width.value && width.value >= 1024
})

const card = ref<HTMLElement | null>(null)
const glow = ref<HTMLElement | null>(null)

// Отступ от краёв карточки для запуска анимации
const OFFSET = 35
let frameId: number | null = null

function handleMouseMove(event: MouseEvent): void {
    if (!card.value || !glow.value || !isDesktop.value) return

    const rect: DOMRect = card.value.getBoundingClientRect()
    const x: number = event.clientX - rect.left
    const y: number = event.clientY - rect.top

    const mouseNotInArea =
        x < OFFSET ||
        y < OFFSET ||
        x > rect.width - OFFSET ||
        y > rect.height - OFFSET

    if (mouseNotInArea) {
        handleMouseLeave()
        return
    }

    const centerX = rect.width / 2
    const centerY = rect.height / 2

    const percentX = (x - centerX) / centerX
    const percentY = -((y - centerY) / centerY)

    if (frameId) {
        cancelAnimationFrame(frameId)
    }

    frameId = requestAnimationFrame(() => {
        if (!card.value || !glow.value) return

        // Добавлен translateZ(0) для принудительного аппаратного ускорения в Safari
        card.value.style.transform = `perspective(1000px) rotateY(${percentX * 10}deg) rotateX(${percentY * 10}deg) scale(1.07) translateZ(0)`
        glow.value.style.opacity = '1'
        glow.value.style.backgroundImage = `
        radial-gradient(
          circle at ${x}px ${y}px,
          #743FDA80,
          #0000000f
        )
      `
    })
}

function handleMouseLeave() {
    if (frameId) {
        cancelAnimationFrame(frameId)
        frameId = null
    }

    if (!card.value || !glow.value || !isDesktop.value) return

    card.value.style.transform = `perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0)`
    glow.value.style.opacity = '0'
}

onMounted(() => {
    isMounted.value = true

    if (!card.value || !isDesktop.value) return

    card.value.addEventListener('mousemove', handleMouseMove)
    card.value.addEventListener('mouseleave', handleMouseLeave)
})

onBeforeUnmount(() => {
    if (!card.value) return

    card.value.removeEventListener('mousemove', handleMouseMove)
    card.value.removeEventListener('mouseleave', handleMouseLeave)

    if (frameId) {
        cancelAnimationFrame(frameId)
    }
})
</script>
